/**
 * Admin Layout
 * Modern layout with collapsible sidebar and clean header
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Toolbar } from '@mui/material';
import Sidebar, { DRAWER_WIDTH_EXPANDED, DRAWER_WIDTH_COLLAPSED } from '../components/Layout/Sidebar';
import Header from '../components/Layout/Header';
import useSidebar, { ReportModeProvider, useReportMode } from '../hooks/useSidebar';
import useAppMode from '../hooks/useAppMode';
import ReportModeView from '../components/ReportModeView';

const AdminLayoutContent = ({ children }) => {
  const {
    isCollapsed,
    mobileOpen,
    toggleCollapse,
    toggleMobile,
    closeMobile,
  } = useSidebar();

  const { reportMode, reportModeData, exitReportMode, handleReportModeSubmit } = useReportMode();
  const { mode: appMode, setMode: setAppMode } = useAppMode();

  const sidebarWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  // Every page's requests carry the current branch. After a hard reload it is
  // restored only once the Header has loaded the branch list, so hold the page
  // until then: a page that loads its data once on open (an order, Result
  // Entry) would otherwise ask before there is a branch and never ask again.
  // With no branches to pick from, show the page anyway.
  const { currentBranch, getRes: branchRes } = useSelector((state) => state.branch);
  const branchReady = !!currentBranch || (branchRes != null && (branchRes.data || []).length === 0);

  // Report Mode: Overlay full-screen view while keeping children mounted to preserve state
  const isReportMode = reportMode && reportModeData;

  return (
    <>
      {isReportMode && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', bgcolor: 'background.default' }}>
          <ReportModeView
            itemData={reportModeData}
            onExit={exitReportMode}
            onSubmitComplete={handleReportModeSubmit}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', visibility: isReportMode ? 'hidden' : 'visible' }}>
        {/* Header */}
        <Header
          onMenuClick={toggleMobile}
          isCollapsed={isCollapsed}
          basePath="/admin"
          appMode={appMode}
          onModeChange={setAppMode}
        />

        {/* Sidebar */}
        <Sidebar
          basePath="/admin"
          isCollapsed={isCollapsed}
          mobileOpen={mobileOpen}
          onToggleCollapse={toggleCollapse}
          onCloseMobile={closeMobile}
          appMode={appMode}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            width: { xs: '100%', lg: `calc(100% - ${sidebarWidth}px)` },
            maxWidth: '100%',
            overflowX: 'hidden',
            transition: 'all 200ms ease',
            bgcolor: 'background.default',
          }}
        >
          {/* Spacer for fixed header */}
          <Toolbar sx={{ mb: { xs: 2, sm: 3 } }} className="main-toolbar" />
          {branchReady ? children : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

const AdminLayout = ({ children }) => {
  return (
    <ReportModeProvider>
      <AdminLayoutContent>{children}</AdminLayoutContent>
    </ReportModeProvider>
  );
};

export default AdminLayout;
